import { Link } from 'react-router-dom';
import { Stack, Text, Group, Image, Rating, Skeleton, Avatar, Button, Anchor, UnstyledButton, Divider } from '@mantine/core';
import { IconPackage, IconUser, IconExternalLink } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { productsService } from '../../services/products';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users';
import { AdCard } from './AdCard';

interface Props {
  productId: string;
}

export function ProductDetailSidebar({ productId }: Props) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [followed, setFollowed] = useState<Set<string>>(new Set());

  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => productsService.getById(productId),
    staleTime: 10 * 60 * 1000,
    enabled: !!productId,
  });

  const affiliateUrl = product?.amazonAffiliateUrl;

  const { data: similar, isLoading: loadingSimilar } = useQuery({
    queryKey: ['product-similar', productId],
    queryFn: () => productsService.getSimilar(productId, 5),
    staleTime: 4 * 60 * 60 * 1000,
    enabled: !!productId,
  });

  const { data: reviewers, isLoading: loadingReviewers } = useQuery({
    queryKey: ['product-reviewers', productId],
    queryFn: () => productsService.getReviewers(productId, 3),
    staleTime: 30 * 60 * 1000,
    enabled: !!productId,
  });

  const followMutation = useMutation({
    mutationFn: (id: string) => usersService.follow(id),
    onSuccess: (_, id) => {
      setFollowed((prev) => new Set(prev).add(id));
      queryClient.invalidateQueries({ queryKey: ['product-reviewers', productId] });
    },
  });

  const visibleReviewers = (reviewers ?? []).filter(
    (r) => r.id !== user?.id && !followed.has(r.id)
  );

  return (
    <Stack gap="lg">
      {/* Amazon Affiliate */}
      {affiliateUrl ? (
        <Button
          component="a"
          href={affiliateUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          fullWidth
          size="sm"
          rightSection={<IconExternalLink size={14} />}
          color="orange"
        >
          View on Amazon (paid link)
        </Button>
      ) : (
        <AdCard />
      )}

      {/* Similar Products */}
      <Divider />
      <Stack gap="xs">
        <Text size="sm" fw={600}>Similar Products</Text>
        <Stack gap="xs">
          {loadingSimilar ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Group key={i} gap="sm" wrap="nowrap">
                <Skeleton w={40} h={40} radius="sm" />
                <Stack gap={4} style={{ flex: 1 }}>
                  <Skeleton h={12} w="80%" />
                  <Skeleton h={10} w="50%" />
                </Stack>
              </Group>
            ))
          ) : !similar?.length ? (
            <Text size="xs" c="dimmed">No similar products yet.</Text>
          ) : (
            similar.map((p) => (
              <UnstyledButton
                key={p.id}
                component={Link}
                to={`/products/${p.id}`}
                style={{ display: 'block', borderRadius: 'var(--mantine-radius-sm)', padding: '0.25rem' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Group gap="sm" wrap="nowrap">
                  {p.imageUrl ? (
                    <Image src={p.imageUrl} w={40} h={40} fit="contain" radius="sm" style={{ flexShrink: 0 }} />
                  ) : (
                    <Group w={40} h={40} justify="center" align="center" style={{ flexShrink: 0, borderRadius: 'var(--mantine-radius-sm)', background: 'var(--mantine-color-default)' }}>
                      <IconPackage size={20} color="var(--mantine-color-dimmed)" />
                    </Group>
                  )}
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} lineClamp={2}>{p.name}</Text>
                    {p.averageRating != null && (
                      <Group gap={4}>
                        <Rating value={p.averageRating} fractions={2} readOnly size="xs" />
                        <Text size="xs" c="dimmed">({p.reviewCount})</Text>
                      </Group>
                    )}
                  </Stack>
                </Group>
              </UnstyledButton>
            ))
          )}
        </Stack>
      </Stack>

      {/* Reviewers to follow */}
      {visibleReviewers.length > 0 && (
        <>
          <Divider />
          <Stack gap="sm">
            <Text size="sm" fw={600}>Reviewers to Follow</Text>
            {loadingReviewers ? (
              Array.from({ length: 2 }).map((_, i) => (
                <Group key={i} justify="space-between">
                  <Group gap="sm"><Skeleton circle h={32} /><Skeleton h={12} w={80} /></Group>
                  <Skeleton h={28} w={60} radius="xl" />
                </Group>
              ))
            ) : (
              visibleReviewers.map((r) => (
                <Group key={r.id} justify="space-between" wrap="nowrap">
                  <Anchor component={Link} to={`/users/${r.username}`} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar src={r.profileImageUrl} size="sm" radius="xl"><IconUser size={14} /></Avatar>
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={1}>@{r.username}</Text>
                        <Text size="xs" c="dimmed">{r.reviewCount} reviews</Text>
                      </Stack>
                    </Group>
                  </Anchor>
                  {isAuthenticated && (
                    <Button size="xs" radius="xl" variant="outline"
                      loading={followMutation.isPending && followMutation.variables === r.id}
                      onClick={() => followMutation.mutate(r.id)}>
                      Follow
                    </Button>
                  )}
                </Group>
              ))
            )}
          </Stack>
        </>
      )}

      <Divider />
      <FooterLinks />
    </Stack>
  );
}

function FooterLinks() {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">
        <Anchor component={Link} to="/privacy" size="xs" c="dimmed">Privacy</Anchor>
        {' · '}
        <Anchor component={Link} to="/terms" size="xs" c="dimmed">Terms</Anchor>
      </Text>
      <Text size="xs" c="dimmed">© {new Date().getFullYear()} CartCritic</Text>
    </Stack>
  );
}
